// src/api/monitoring.ts
// Real Monitoring API - Connected to Backend via API Gateway
// All endpoints use /api/v1/monitoring prefix (e.g., /api/v1/monitoring/projects)

/**
 * ⚠️ IMPORTANT: Backend Header Requirements vs CORS Configuration
 *
 * The backend documentation states it requires these headers:
 * - X-User-ID
 * - X-Organization-ID
 * - X-User-Role
 * - X-User-Email
 *
 * However, the backend CORS configuration only allows:
 * - Content-Type
 * - Authorization
 * - X-Requested-With
 *
 * SOLUTION: The backend MUST extract user/org context from the JWT token
 * instead of requiring custom headers. This is the standard approach and
 * prevents CORS errors in production (Netlify → Railway).
 *
 * If the backend truly needs custom headers, update Railway CORS config:
 * allow_headers=["Content-Type", "Authorization", "X-Requested-With",
 *                "X-User-ID", "X-Organization-ID", "X-User-Role", "X-User-Email"]
 */

import type {
  ProjectOut,
  SnapshotOut,
  ChangeOut,
  TestSitemapResponse,
  ChangeStats,
  ChangeSummary,
  ProjectSchedule,
  ProjectSettings,
  CrawlStatus,
  CrawlJobsResponse,
  ActiveCrawl,
  TestWebhookResponse,
  URLHistoryResponse,
  SitemapDiscoveryResponse,
  PaginatedSnapshotsResponse,
  PaginatedChangesResponse,
  ScheduleReport
} from './types';
import { ApiService } from '../auth/services/apiService';

export const CRAWL_ALREADY_RUNNING_CODE = 'CRAWL_ALREADY_RUNNING';
const BACKEND_CRAWL_ALREADY_RUNNING_ERROR = 'crawl_already_running';

export interface MonitoringApiError extends Error {
  code?: string;
  statusCode?: number;
  details?: unknown;
  retryAfter?: number;
}

function createMonitoringApiError(
  message: string,
  code: string,
  statusCode: number,
  details?: unknown,
): MonitoringApiError {
  const apiError = new Error(message) as MonitoringApiError;
  apiError.code = code;
  apiError.statusCode = statusCode;
  apiError.details = details;
  return apiError;
}

// Create a specialized monitoring API service that handles direct responses
class MonitoringApiService extends ApiService {
  constructor() {
    super();
    // In development, use empty string to let Vite proxy handle the requests
    // In production, use the full API URL
    const isDevelopment = import.meta.env.DEV;
    (this as any).baseURL = isDevelopment ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:8000');
  }

  // REMOVED: Custom enterprise headers that cause CORS errors
  // The backend doesn't allow X-User-ID, X-Organization-ID, X-User-Role, X-User-Permissions
  // Only Authorization, Content-Type, and X-Requested-With are allowed
  // User/org context is extracted from the JWT token by the backend

  // Custom method to handle direct API responses (not wrapped in {data: ...})
  async directRequest<T = any>(endpoint: string, options: any = {}): Promise<T> {
    const { skipAuth = false, skipRefresh = false, ...requestOptions } = options;

    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(requestOptions.headers as Record<string, string> || {}),
    };

    // Add authentication header if not skipped
    // Follow auth-truth.md Pattern 1: Direct localStorage access
    if (!skipAuth) {
      const accessToken = localStorage.getItem('access_token');
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
      // User/org context is extracted from JWT token by backend
      // No custom headers needed (they cause CORS errors)
    }

    const config: RequestInit = {
      ...requestOptions,
      headers,
    };

    const url = `${(this as any).baseURL}${endpoint}`;

    try {
      const response = await fetch(url, config);

      logger.log(`🌐 MonitoringAPI: ${requestOptions.method || 'GET'} ${url} - Status: ${response.status}`);
      logger.log('🔒 MonitoringAPI Headers:', Object.keys(headers).map(key => `${key}: ${key.includes('Authorization') ? '***' : headers[key]}`).join(', '));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Handle 401 Unauthorized - try token refresh
        if (response.status === 401 && !skipAuth && !skipRefresh) {
          logger.log('🔄 MonitoringAPI: Token expired (401), attempting refresh...');

          // Import dynamically to avoid circular dependencies. The
          // coordinated refresher persists the rotated tokens and, on
          // failure, clears storage and dispatches auth:global_token_expired
          // itself.
          const { refreshAccessToken } = await import('../auth/services/tokenRefresh');
          const refreshed = await refreshAccessToken('MonitoringApiService');

          if (refreshed) {
            logger.log('✅ MonitoringAPI: Token refreshed successfully, retrying request...');

            // Retry the original request with new token
            return this.directRequest<T>(endpoint, { ...options, skipRefresh: true });
          }

          logger.error('❌ MonitoringAPI: Token refresh failed');
          throw new Error('Session expired. Please log in again.');
        }

        // 🆕 Handle insufficient credits (402 Payment Required)
        if (response.status === 402) {
          // Check if this is an insufficient credits error
          if (errorData.detail && typeof errorData.detail === 'object' && errorData.detail.error === 'insufficient_credits') {
            const insufficientCreditsError: any = new Error(errorData.detail.message || 'Insufficient credits');
            insufficientCreditsError.code = 'INSUFFICIENT_CREDITS';
            insufficientCreditsError.statusCode = 402;
            insufficientCreditsError.details = errorData.detail;

            // Dispatch insufficient credits event for UI notification
            window.dispatchEvent(new CustomEvent('credits:insufficient', {
              detail: {
                message: errorData.detail.message,
                required_credits: errorData.detail.required_credits,
                project_name: errorData.detail.project_name,
                upgrade_url: errorData.detail.upgrade_url
              }
            }));

            throw insufficientCreditsError;
          }

          throw createMonitoringApiError(
            errorData.detail?.message || errorData.message || 'Payment required',
            'PAYMENT_REQUIRED',
            402,
            errorData.detail,
          );
        }

        // 🆕 Enterprise: Handle rate limiting (429)
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');

          // Check if this is a quota exceeded error
          if (errorData.detail && typeof errorData.detail === 'object' && errorData.detail.error === 'Quota exceeded') {
            const quotaError: any = new Error(errorData.detail.message || 'Quota exceeded');
            quotaError.code = 'QUOTA_EXCEEDED';
            quotaError.statusCode = 429;
            quotaError.details = errorData.detail;

            // Dispatch quota exceeded event for UI notification
            window.dispatchEvent(new CustomEvent('quota:exceeded', {
              detail: {
                message: errorData.detail.message,
                credits_remaining: errorData.detail.credits_remaining,
                credits_limit: errorData.detail.credits_limit,
                credits_used: errorData.detail.credits_used
              }
            }));

            throw quotaError;
          }

          // Regular rate limit error
          const rateLimitError: any = new Error(
            errorData.detail || `Rate limit exceeded. Please try again in ${retryAfter || '60'} seconds.`
          );
          rateLimitError.code = 'RATE_LIMIT_EXCEEDED';
          rateLimitError.retryAfter = retryAfter ? parseInt(retryAfter) : 60;
          rateLimitError.statusCode = 429;
          throw rateLimitError;
        }

        const detail = errorData.detail;

        if (
          response.status === 409 &&
          detail &&
          typeof detail === 'object' &&
          detail.error === BACKEND_CRAWL_ALREADY_RUNNING_ERROR
        ) {
          throw createMonitoringApiError(
            detail.message || 'A crawl is already in progress for this project.',
            CRAWL_ALREADY_RUNNING_CODE,
            response.status,
            detail,
          );
        }

        if (detail && typeof detail === 'object') {
          throw createMonitoringApiError(
            detail.message || errorData.message || `HTTP ${response.status}: ${response.statusText}`,
            detail.error ? String(detail.error) : `HTTP_${response.status}`,
            response.status,
            detail,
          );
        }

        throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      logger.log('✅ MonitoringAPI Response:', data);
      return data; // Return data directly, not wrapped in .data property
    } catch (error: any) {
      logger.error('❌ MonitoringAPI request failed:', error);

      // 🆕 Enterprise: Add user-friendly error messages
      if (error.code === 'RATE_LIMIT_EXCEEDED') {
        logger.warn(`⏱️ Rate limit hit. Retry after ${error.retryAfter} seconds`);
      }

      throw error;
    }
  }

  // Override the convenience methods to use directRequest
  async get<T = any>(endpoint: string, options?: any): Promise<T> {
    return this.directRequest<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T = any>(endpoint: string, data?: any, options?: any): Promise<T> {
    return this.directRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(endpoint: string, data?: any, options?: any): Promise<T> {
    return this.directRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string, options?: any): Promise<T> {
    return this.directRequest<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

const monitoringApiService = new MonitoringApiService();

// Status mapping from backend to frontend (case-insensitive)
const statusMapping = {
  'pending': 'active',    // Project ready, awaiting crawl
  'running': 'active',    // Currently crawling
  'completed': 'active',  // Crawl complete, ready for next
  'done': 'active',       // Backend uses "DONE" instead of "completed"
  'failed': 'error',      // Crawl failed
  'paused': 'paused'      // Project paused by user
} as const;

// Helper function to normalize status (handle case-insensitive mapping)
function normalizeStatus(status: string): keyof typeof statusMapping {
  const lowercaseStatus = status.toLowerCase();
  // Handle the case where the status might not be in our mapping
  if (lowercaseStatus in statusMapping) {
    return lowercaseStatus as keyof typeof statusMapping;
  }
  // Default to 'failed' for unknown statuses
  return 'failed';
}

// Type for frontend monitoring project
export interface MonitoringProject {
  id: string;
  createdNew?: boolean;
  name: string;
  url: string;
  webhookUrl?: string | null; // 🆕 Enterprise: Webhook URL
  status: 'active' | 'paused' | 'error';
  schedule: {
    frequency: 'hourly' | 'daily' | '3days' | 'weekly';
    interval: number;
    enabled: boolean;
    timezone: 'UTC';
  };
  alerts: never[]; // Disabled for now
  lastSnapshot?: string;
  lastCheck?: string;
  specificUrls?: string[];
  scopeType?: string;
  crawlStatus?: string;
  changeCount: number;
  alertCount: number;
  createdAt: string;
  updatedAt?: string;
  lastError?: string | null;
}

// Convert backend ProjectOut to frontend MonitoringProject
function mapProjectToMonitoringProject(project: ProjectOut): MonitoringProject {
  const normalizedStatus = normalizeStatus(project.status);

  // Parse frequency from API response, default to 'daily' if invalid
  let frequency: 'hourly' | 'daily' | '3days' | 'weekly' = 'daily';
  if (project.crawl_frequency === 'hourly' || project.crawl_frequency === 'daily' || project.crawl_frequency === '3days' || project.crawl_frequency === 'weekly') {
    frequency = project.crawl_frequency as 'hourly' | 'daily' | '3days' | 'weekly';
  }

  return {
    id: project.id, // Already a string UUID
    createdNew: project.created_new,
    name: project.name,
    url: project.sitemap_url || '', // Empty string if null (for specific_urls projects)
    webhookUrl: project.webhook_url || null, // 🆕 Enterprise: Webhook URL
    status: statusMapping[normalizedStatus] || 'error',
    schedule: {
      frequency: frequency,
      interval: 1,
      enabled: normalizedStatus !== 'failed',
      timezone: 'UTC'
    },
    alerts: [], // Disabled for now
    lastSnapshot: undefined, // Will be populated from snapshots if available
    lastCheck: project.last_crawl_at || undefined, // Use actual last crawl time
    specificUrls: project.settings?.monitoring_scope?.specific_urls || [],
    scopeType: project.settings?.monitoring_scope?.scope_type,
    crawlStatus: normalizedStatus,
    changeCount: project.total_changes || 0, // Use actual change count
    alertCount: 0, // Disabled for now
    createdAt: project.created_at,
    updatedAt: project.created_at,
    lastError: project.last_error
  };
}

export async function getProjects(): Promise<MonitoringProject[]> {
  try {
    // Backend base URL is /api/v1/monitoring, endpoint is /projects
    const projects: ProjectOut[] = await monitoringApiService.get('/api/v1/monitoring/projects');
    return projects.map(mapProjectToMonitoringProject);
  } catch (error: any) {
    logger.error('Failed to fetch monitoring projects:', error);
    // Preserve the original error structure for better error handling
    throw {
      code: error.code || 'SERVER_ERROR',
      message: error.message || 'Failed to fetch monitoring projects',
      details: error.details
    };
  }
}

export async function getProject(id: string): Promise<MonitoringProject> {
  try {
    const project: ProjectOut = await monitoringApiService.get(`/api/v1/monitoring/projects/${id}`);
    return mapProjectToMonitoringProject(project);
  } catch (error: any) {
    logger.error(`Failed to fetch monitoring project ${id}:`, error);
    throw {
      code: error.code || 'SERVER_ERROR',
      message: error.message || `Failed to fetch monitoring project ${id}`,
      details: error.details
    };
  }
}

export async function createProject(data: {
  name: string;
  url?: string;
  sitemap_urls?: string[];
  webhook_url?: string;
  custom_headers?: Record<string, string>;
  scope_type?: import('./types').ScopeType;
  specific_urls?: string[];
  segment_filters?: import('./types').SegmentFilter[];
  max_urls?: number;
  crawl_frequency?: 'daily' | 'weekly';
  onboarding_intent_id?: string;
}): Promise<MonitoringProject> {
  try {
    const requestData: any = {
      name: data.name,
    };
    if (data.crawl_frequency) requestData.crawl_frequency = data.crawl_frequency;
    if (data.onboarding_intent_id) requestData.onboarding_intent_id = data.onboarding_intent_id;

    // Add sitemap_url (optional for specific_urls mode)
    if (data.url) {
      requestData.sitemap_url = data.url;
    }

    // Add multiple sitemap URLs if provided
    if (data.sitemap_urls && data.sitemap_urls.length > 1) {
      requestData.sitemap_urls = data.sitemap_urls;
    }

    // Add webhook URL if provided (🆕 Enterprise)
    if (data.webhook_url) {
      requestData.webhook_url = data.webhook_url;
    }

    // Add custom headers if provided
    if (data.custom_headers && Object.keys(data.custom_headers).length > 0) {
      requestData.custom_headers = data.custom_headers;
    }

    // 🎯 NEW: Add monitoring_scope during project creation (one-step flow)
    if (data.scope_type) {
      requestData.monitoring_scope = {
        scope_type: data.scope_type,
        ...(data.segment_filters && data.segment_filters.length > 0 ? { segment_filters: data.segment_filters } : {}),
        ...(data.specific_urls && data.specific_urls.length > 0 ? { specific_urls: data.specific_urls } : {}),
        ...(data.max_urls ? { max_urls: data.max_urls } : {})
      };
    }

    const project: ProjectOut = await monitoringApiService.post('/api/v1/monitoring/projects', requestData);
    return mapProjectToMonitoringProject(project);
  } catch (error: any) {
    logger.error('Failed to create monitoring project:', error);
    throw error;
  }
}

export async function updateProject(id: string, data: {
  name?: string;
  url?: string;
  sitemap_urls?: string[];       // Additional sitemaps beyond the primary one
  webhook_url?: string | null;
  custom_headers?: Record<string, string>;
  user_agent?: string;           // Convenience: stored as custom_headers["User-Agent"]
  specific_urls?: string[];      // Individual URLs to monitor (merged into monitoring scope)
}): Promise<MonitoringProject> {
  try {
    const updateData: any = {};
    if (data.name !== undefined) updateData.name = data.name;
    if (data.url !== undefined) updateData.sitemap_url = data.url;
    if (data.sitemap_urls !== undefined) updateData.sitemap_urls = data.sitemap_urls;
    if (data.webhook_url !== undefined) updateData.webhook_url = data.webhook_url;
    if (data.custom_headers !== undefined) updateData.custom_headers = data.custom_headers;
    if (data.user_agent !== undefined) updateData.user_agent = data.user_agent;
    if (data.specific_urls !== undefined) updateData.specific_urls = data.specific_urls;

    const project: ProjectOut = await monitoringApiService.put(`/api/v1/monitoring/projects/${id}`, updateData);
    return mapProjectToMonitoringProject(project);
  } catch (error: any) {
    logger.error(`Failed to update monitoring project ${id}:`, error);
    throw {
      code: error.code || 'SERVER_ERROR',
      message: error.message || `Failed to update monitoring project ${id}`,
      details: error.details
    };
  }
}

export async function deleteProject(id: string): Promise<void> {
  try {
    await monitoringApiService.delete(`/api/v1/monitoring/projects/${id}`);
  } catch (error) {
    logger.error(`Failed to delete monitoring project ${id}:`, error);
    throw new Error(`Failed to delete monitoring project ${id}`);
  }
}

export async function getSnapshots(projectId: string): Promise<SnapshotOut[]> {
  try {
    logger.log(`🔍 Fetching snapshots for project: ${projectId}`);
    const response = await monitoringApiService.get(`/api/v1/monitoring/projects/${projectId}/snapshots`);

    // Handle paginated response format
    if (response && typeof response === 'object' && 'items' in response) {
      const snapshots = (response as { items: SnapshotOut[] }).items;
      logger.log(`✅ Retrieved ${snapshots.length} snapshots for project ${projectId}`);
      return snapshots;
    }

    // Handle legacy array format
    if (Array.isArray(response)) {
      logger.log(`✅ Retrieved ${response.length} snapshots for project ${projectId}`);
      return response;
    }

    logger.warn(`⚠️ Unexpected response format for snapshots`);
    return [];
  } catch (error: any) {
    logger.error(`❌ Failed to fetch snapshots for project ${projectId}:`, error);

    // Enhanced error handling
    if (error.message?.includes('404')) {
      throw new Error(`Project ${projectId} not found or no snapshots available`);
    } else if (error.message?.includes('401') || error.message?.includes('403')) {
      throw new Error('Authentication required to access snapshots');
    } else {
      throw new Error(`Failed to fetch snapshots: ${error.message || 'Unknown error'}`);
    }
  }
}

export async function getChanges(projectId: string): Promise<ChangeOut[]> {
  try {
    logger.log(`🔍 Fetching changes for project: ${projectId}`);
    const response = await monitoringApiService.get(`/api/v1/monitoring/projects/${projectId}/changes`);

    // Handle paginated response format
    if (response && typeof response === 'object' && 'items' in response) {
      const changes = (response as { items: ChangeOut[] }).items;
      logger.log(`✅ Retrieved ${changes.length} changes for project ${projectId}`);
      return changes;
    }

    // Handle legacy array format
    if (Array.isArray(response)) {
      logger.log(`✅ Retrieved ${response.length} changes for project ${projectId}`);
      return response;
    }

    logger.warn(`⚠️ Unexpected response format for changes`);
    return [];
  } catch (error: any) {
    logger.error(`❌ Failed to fetch changes for project ${projectId}:`, error);

    // Enhanced error handling
    if (error.message?.includes('404')) {
      throw new Error(`Project ${projectId} not found or no changes available`);
    } else if (error.message?.includes('401') || error.message?.includes('403')) {
      throw new Error('Authentication required to access changes');
    } else {
      throw new Error(`Failed to fetch changes: ${error.message || 'Unknown error'}`);
    }
  }
}

export interface PartialCrawlOptions {
  /** Explicit page URLs to crawl (partial/targeted crawl). */
  urls?: string[];
  /** Sitemap URLs to expand then crawl (partial/targeted crawl). */
  sitemap_urls?: string[];
}

export async function triggerCrawl(
  projectId: string,
  partial?: PartialCrawlOptions,
  force?: boolean,
): Promise<MonitoringProject> {
  try {
    const isPartial = partial && (partial.urls?.length || partial.sitemap_urls?.length);
    const baseEndpoint = isPartial
      ? `/api/v1/monitoring/projects/${projectId}/crawl/partial`
      : `/api/v1/monitoring/projects/${projectId}/crawl`;
    const endpoint = (!isPartial && force) ? `${baseEndpoint}?force=true` : baseEndpoint;
    const project: ProjectOut = await monitoringApiService.post(
      endpoint,
      isPartial ? partial : undefined,
    );
    return mapProjectToMonitoringProject(project);
  } catch (error: any) {
    logger.error(`Failed to trigger crawl for project ${projectId}:`, error);

    // Preserve specific error codes (insufficient credits, quota exceeded, etc.)
    if (
      error.code === 'INSUFFICIENT_CREDITS' ||
      error.code === 'QUOTA_EXCEEDED' ||
      error.code === CRAWL_ALREADY_RUNNING_CODE
    ) {
      throw error; // Re-throw with all details intact
    }

    // For other errors, wrap with context
    throw new Error(`Failed to trigger crawl for project ${projectId}`);
  }
}

// Test sitemap URL functionality
export async function testUrl(url: string): Promise<TestSitemapResponse> {
  try {
    // Basic URL validation first
    new URL(url);
    
    // Call the backend test-sitemap endpoint
    const response: TestSitemapResponse = await monitoringApiService.post('/api/v1/monitoring/test-sitemap', {
      sitemap_url: url
    });
    
    return response;
  } catch (error: any) {
    logger.error('Failed to test sitemap URL:', error);
    
    // Handle specific error cases
    if (error.message?.includes('Invalid URL format')) {
      return {
        is_valid: false,
        message: 'Invalid URL format',
        urls_found: null,
        sample_urls: null,
        errors: ['Please enter a valid URL']
      };
    }
    
    return {
      is_valid: false,
      message: error.message || 'Failed to test sitemap URL',
      urls_found: null,
      sample_urls: null,
      errors: [error.message || 'Unknown error occurred']
    };
  }
}

export async function acknowledgeChange(changeId: string): Promise<void> {
  try {
    await monitoringApiService.put(`/api/v1/monitoring/changes/${encodeURIComponent(changeId)}/acknowledge`);
  } catch (error) {
    logger.error(`Failed to acknowledge change ${changeId}:`, error);
    throw new Error(`Failed to acknowledge change ${changeId}`);
  }
}

// ============================================================================
// NEW: Crawl Job Tracking & Progress Monitoring
// ============================================================================

/**
 * Get real-time crawl progress for a project
 * Use this to show live progress bars during active crawls
 * Poll every 2-5 seconds while status is 'running'
 */
export async function getCrawlStatus(projectId: string): Promise<CrawlStatus> {
  try {
    logger.log(`🔍 Fetching crawl status for project: ${projectId}`);
    const status: CrawlStatus = await monitoringApiService.get(`/api/v1/monitoring/projects/${projectId}/crawl/status`);
    logger.log(`✅ Crawl status: ${status.status} (${status.progress_percentage}%)`);
    return status;
  } catch (error: any) {
    logger.error(`❌ Failed to fetch crawl status for project ${projectId}:`, error);
    throw new Error(`Failed to fetch crawl status: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Get crawl job history for a project
 * Shows past crawl executions with results and statistics
 */
export async function getCrawlJobs(
  projectId: string,
  page: number = 1,
  pageSize: number = 10
): Promise<CrawlJobsResponse> {
  try {
    logger.log(`🔍 Fetching crawl jobs for project: ${projectId} (page ${page})`);
    const response: CrawlJobsResponse = await monitoringApiService.get(
      `/api/v1/monitoring/projects/${projectId}/crawl/jobs?page=${page}&page_size=${pageSize}`
    );
    logger.log(`✅ Retrieved ${response.jobs.length} crawl jobs (total: ${response.total})`);
    return response;
  } catch (error: any) {
    logger.error(`❌ Failed to fetch crawl jobs for project ${projectId}:`, error);
    throw new Error(`Failed to fetch crawl jobs: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Get all active crawls across all projects
 * Use this for dashboard overview of running crawls
 */
export async function getActiveCrawls(): Promise<ActiveCrawl[]> {
  try {
    logger.log(`🔍 Fetching all active crawls`);
    const crawls: ActiveCrawl[] = await monitoringApiService.get(`/api/v1/monitoring/crawl/active`);
    logger.log(`✅ Retrieved ${crawls.length} active crawls`);
    return crawls;
  } catch (error: any) {
    logger.error(`❌ Failed to fetch active crawls:`, error);
    throw new Error(`Failed to fetch active crawls: ${error.message || 'Unknown error'}`);
  }
}

/**
 * 🛑 Cancel a running crawl job
 * Stops a crawl job that is currently running or pending
 */
export async function cancelCrawlJob(jobId: string): Promise<void> {
  try {
    logger.log(`🛑 Cancelling crawl job: ${jobId}`);
    await monitoringApiService.post(`/api/v1/monitoring/crawl/jobs/${jobId}/cancel`);
    logger.log(`✅ Crawl job cancelled successfully`);
  } catch (error: any) {
    logger.error(`❌ Failed to cancel crawl job ${jobId}:`, error);
    throw new Error(`Failed to cancel crawl job: ${error.message || 'Unknown error'}`);
  }
}

// NEW API FUNCTIONS FOR ENHANCED FEATURES

// Project Control Functions
export async function pauseProject(projectId: string): Promise<void> {
  try {
    await monitoringApiService.post(`/api/v1/monitoring/projects/${projectId}/pause`);
  } catch (error) {
    logger.error(`Failed to pause project ${projectId}:`, error);
    throw new Error(`Failed to pause project ${projectId}`);
  }
}

export async function resumeProject(projectId: string): Promise<void> {
  try {
    await monitoringApiService.post(`/api/v1/monitoring/projects/${projectId}/resume`);
  } catch (error) {
    logger.error(`Failed to resume project ${projectId}:`, error);
    throw new Error(`Failed to resume project ${projectId}`);
  }
}

// Project Settings Management
export async function getProjectSettings(projectId: string): Promise<{ settings: ProjectSettings; crawl_frequency: string; is_active: boolean }> {
  try {
    return await monitoringApiService.get(`/api/v1/monitoring/projects/${projectId}/settings`);
  } catch (error) {
    logger.error(`Failed to get project settings ${projectId}:`, error);
    throw new Error(`Failed to get project settings ${projectId}`);
  }
}

export async function updateProjectSettings(
  projectId: string,
  settings: ProjectSettings
): Promise<void> {
  try {
    await monitoringApiService.put(`/api/v1/monitoring/projects/${projectId}/settings`, settings);
  } catch (error) {
    logger.error(`Failed to update project settings ${projectId}:`, error);
    throw new Error(`Failed to update project settings ${projectId}`);
  }
}

// Schedule Management
export async function getProjectSchedule(projectId: string): Promise<ProjectSchedule> {
  try {
    return await monitoringApiService.get(`/api/v1/monitoring/projects/${projectId}/schedule`);
  } catch (error) {
    logger.error(`Failed to get project schedule ${projectId}:`, error);
    throw new Error(`Failed to get project schedule ${projectId}`);
  }
}

export async function updateProjectSchedule(
  projectId: string,
  schedule: Partial<ProjectSchedule>
): Promise<void> {
  try {
    await monitoringApiService.put(`/api/v1/monitoring/projects/${projectId}/schedule`, schedule);
  } catch (error) {
    logger.error(`Failed to update project schedule ${projectId}:`, error);
    throw new Error(`Failed to update project schedule ${projectId}`);
  }
}

// Enhanced Analytics Functions
export async function getChangeStats(projectId: string): Promise<ChangeStats> {
  try {
    return await monitoringApiService.get(`/api/v1/monitoring/projects/${projectId}/changes/stats`);
  } catch (error) {
    logger.error(`Failed to get change statistics ${projectId}:`, error);
    throw new Error(`Failed to get change statistics ${projectId}`);
  }
}

export async function getChangeSummary(projectId: string): Promise<ChangeSummary> {
  try {
    return await monitoringApiService.get(`/api/v1/monitoring/projects/${projectId}/changes/summary`);
  } catch (error) {
    logger.error(`Failed to get change summary ${projectId}:`, error);
    throw new Error(`Failed to get change summary ${projectId}`);
  }
}

export async function getUnacknowledgedChanges(limit = 50): Promise<ChangeOut[]> {
  try {
    return await monitoringApiService.get(`/api/v1/monitoring/changes/unacknowledged?page=1&page_size=${limit}`);
  } catch (error) {
    logger.error('Failed to get unacknowledged changes:', error);
    throw new Error('Failed to get unacknowledged changes');
  }
}

// ============================================================================
// 📊 SCHEDULE REPORT
// ============================================================================

/**
 * Get schedule report for a project
 * Shows when next crawl will happen and which pages will be crawled
 */
export async function getScheduleReport(projectId: string): Promise<ScheduleReport> {
  try {
    logger.log(`📊 Getting schedule report for project ${projectId}`);
    const response: ScheduleReport = await monitoringApiService.get(
      `/api/v1/monitoring/projects/${projectId}/schedule-report`
    );
    logger.log(`✅ Schedule report retrieved successfully`);
    return response;
  } catch (error: any) {
    logger.error(`❌ Failed to get schedule report for project ${projectId}:`, error);
    throw new Error(`Failed to get schedule report: ${error.message || 'Unknown error'}`);
  }
}

// ============================================================================
// 🎯 TIER MANAGEMENT FUNCTIONS
// ============================================================================

/**
 * Set manual tier for a specific page
 * Overrides the auto-calculated tier based on importance score
 */
export async function setPageTier(
  projectId: string,
  pageId: string,
  tier: 'A' | 'B' | 'C'
): Promise<void> {
  try {
    logger.log(`🎯 Setting tier ${tier} for page ${pageId} in project ${projectId}`);
    await monitoringApiService.post(
      `/api/v1/monitoring/projects/${projectId}/pages/${pageId}/set-tier?tier=${tier}`
    );
    logger.log(`✅ Tier set successfully`);
  } catch (error: any) {
    logger.error(`❌ Failed to set tier for page ${pageId}:`, error);
    throw new Error(`Failed to set tier: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Clear manual tier override for a specific page
 * Reverts to using the auto-calculated tier
 */
export async function clearPageTier(
  projectId: string,
  pageId: string
): Promise<void> {
  try {
    logger.log(`🎯 Clearing manual tier for page ${pageId} in project ${projectId}`);
    await monitoringApiService.delete(
      `/api/v1/monitoring/projects/${projectId}/pages/${pageId}/tier`
    );
    logger.log(`✅ Manual tier cleared successfully`);
  } catch (error: any) {
    logger.error(`❌ Failed to clear tier for page ${pageId}:`, error);
    throw new Error(`Failed to clear tier: ${error.message || 'Unknown error'}`);
  }
}

// Helper function to get common header templates
export function getCommonHeaderTemplates(): Record<string, Record<string, string>> {
  return {
    'Chrome Desktop': {
      'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
      'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,image/apng,*/*;q=0.8',
      'Accept-Language': 'en-US,en;q=0.9',
      'Accept-Encoding': 'gzip, deflate, br',
      'DNT': '1',
      'Connection': 'keep-alive',
      'Upgrade-Insecure-Requests': '1'
    },
    'Firefox Desktop': {
      'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:121.0) Gecko/20100101 Firefox/121.0',
      'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,*/*;q=0.8',
      'Accept-Language': 'en-US,en;q=0.5',
      'Accept-Encoding': 'gzip, deflate, br',
      'DNT': '1',
      'Connection': 'keep-alive',
      'Upgrade-Insecure-Requests': '1'
    },
    'Safari Desktop': {
      'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Version/17.0 Safari/537.36',
      'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
      'Accept-Language': 'en-US,en;q=0.9',
      'Accept-Encoding': 'gzip, deflate, br'
    },
    'Mobile Chrome': {
      'User-Agent': 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.0 Mobile/15E148 Safari/604.1',
      'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
      'Accept-Language': 'en-US,en;q=0.9'
    }
  };
}

// Helper function to validate custom headers
export function validateCustomHeaders(headers: Record<string, string>): {
  isValid: boolean;
  warnings: string[];
  errors: string[];
} {
  const warnings: string[] = [];
  const errors: string[] = [];
  
  // Security warnings for sensitive headers
  const sensitiveHeaders = ['authorization', 'cookie', 'x-api-key', 'x-auth-token'];
  const headerKeys = Object.keys(headers).map(key => key.toLowerCase());
  
  sensitiveHeaders.forEach(sensitiveHeader => {
    if (headerKeys.includes(sensitiveHeader)) {
      warnings.push(
        `⚠️ Header "${sensitiveHeader}" contains sensitive data that will be stored unencrypted`
      );
    }
  });
  
  // Validate header format
  Object.entries(headers).forEach(([key, value]) => {
    // Basic header name validation
    if (!/^[a-zA-Z0-9\-_]+$/.test(key)) {
      errors.push(`Invalid header name: "${key}". Use only alphanumeric characters, hyphens, and underscores.`);
    }
    
    // Check for empty values
    if (!value || value.trim() === '') {
      errors.push(`Header "${key}" has empty value`);
    }
    
    // Check for very long values (potential issue)
    if (value.length > 2000) {
      warnings.push(`Header "${key}" is very long (${value.length} characters). This may cause issues.`);
    }
  });
  
  // Check total header count
  if (Object.keys(headers).length > 15) {
    warnings.push(`${Object.keys(headers).length} headers defined. Consider reducing for better performance.`);
  }
  
  return {
    isValid: errors.length === 0,
    warnings,
    errors
  };
}

// ============================================================================
// 🆕 ENTERPRISE FEATURES - Webhooks
// ============================================================================

/**
 * Test webhook delivery for a project
 * Sends a test event to the configured webhook URL
 */
export async function testWebhook(projectId: string): Promise<TestWebhookResponse> {
  try {
    logger.log(`🔔 Testing webhook for project: ${projectId}`);
    const response: TestWebhookResponse = await monitoringApiService.post(
      `/api/v1/monitoring/projects/${projectId}/test-webhook`
    );
    logger.log(`✅ Webhook test result:`, response);
    return response;
  } catch (error: any) {
    logger.error(`❌ Failed to test webhook for project ${projectId}:`, error);
    throw new Error(`Failed to test webhook: ${error.message || 'Unknown error'}`);
  }
}

// ============================================================================
// 🆕 URL HISTORY & TIMELINE
// ============================================================================

/**
 * Get complete history for a specific URL within a project
 * Includes timeline, field history, stability metrics, and e-commerce data
 */
export async function getURLHistory(
  projectId: string,
  url: string,
  limit: number = 100
): Promise<URLHistoryResponse> {
  try {
    const encodedUrl = encodeURIComponent(url);
    logger.log(`🔍 Fetching URL history for: ${url} in project ${projectId}`);
    const response: URLHistoryResponse = await monitoringApiService.get(
      `/api/v1/monitoring/projects/${projectId}/url-history?url=${encodedUrl}&limit=${limit}`
    );
    logger.log(`✅ Retrieved URL history with ${response.timeline.length} timeline entries`);
    return response;
  } catch (error: any) {
    logger.error(`❌ Failed to fetch URL history:`, error);
    throw new Error(`Failed to fetch URL history: ${error.message || 'Unknown error'}`);
  }
}

// ============================================================================
// 🆕 SMART SITEMAP DISCOVERY
// ============================================================================

/**
 * Discover sitemaps from a website URL
 * Automatically finds sitemap.xml, sitemap_index.xml, and other common locations
 */
export async function discoverSitemaps(websiteUrl: string): Promise<SitemapDiscoveryResponse> {
  try {
    logger.log(`🔍 Discovering sitemaps for: ${websiteUrl}`);
    const response: SitemapDiscoveryResponse = await monitoringApiService.post(
      '/api/v1/monitoring/discover-sitemaps',
      { website_url: websiteUrl }
    );
    logger.log(`✅ Found ${response.sitemaps_found.length} sitemaps`);
    return response;
  } catch (error: any) {
    logger.error(`❌ Failed to discover sitemaps:`, error);
    // Return a graceful fallback instead of throwing
    return {
      success: false,
      website_url: websiteUrl,
      sitemaps_found: [],
      total_urls: 0,
      discovery_method: 'error',
      message: 'Failed to discover sitemaps',
      errors: [error.message || 'Unknown error'],
      suggestions: [
        `${websiteUrl}/sitemap.xml`,
        `${websiteUrl}/sitemap_index.xml`,
        `${websiteUrl}/sitemap/sitemap.xml`
      ],
      show_manual_input_popup: true,
      manual_input_options: []
    };
  }
}

// ============================================================================
// 🆕 CSV EXPORT FUNCTIONS
// ============================================================================

/**
 * Export all snapshots for a project as CSV
 * Returns a Blob that can be downloaded
 */
export async function exportSnapshotsCSV(projectId: string): Promise<Blob> {
  try {
    logger.log(`📥 Exporting snapshots CSV for project: ${projectId}`);
    const accessToken = localStorage.getItem('access_token');
    const baseURL = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:8000');

    const response = await fetch(
      `${baseURL}/api/v1/monitoring/projects/${projectId}/snapshots/export/csv`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    logger.log(`✅ CSV export successful (${(blob.size / 1024).toFixed(2)} KB)`);
    return blob;
  } catch (error: any) {
    logger.error(`❌ Failed to export snapshots CSV:`, error);
    throw new Error(`Failed to export CSV: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Export all snapshots for a project as Excel (.xlsx)
 * Recommended for Windows users - styled headers, auto-filters, proper encoding
 */
export async function exportSnapshotsExcel(projectId: string): Promise<Blob> {
  try {
    logger.log(`📥 Exporting snapshots Excel for project: ${projectId}`);
    const accessToken = localStorage.getItem('access_token');
    const baseURL = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:8000');

    const response = await fetch(
      `${baseURL}/api/v1/monitoring/projects/${projectId}/snapshots/export/excel`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    logger.log(`✅ Excel export successful (${(blob.size / 1024).toFixed(2)} KB)`);
    return blob;
  } catch (error: any) {
    logger.error(`❌ Failed to export snapshots Excel:`, error);
    throw new Error(`Failed to export Excel: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Export all changes for a project as CSV
 */
export async function exportChangesCSV(projectId: string): Promise<Blob> {
  try {
    logger.log(`📥 Exporting changes CSV for project: ${projectId}`);
    const accessToken = localStorage.getItem('access_token');
    const baseURL = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:8000');

    const response = await fetch(
      `${baseURL}/api/v1/monitoring/projects/${projectId}/changes/export/csv`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    logger.log(`✅ CSV export successful (${(blob.size / 1024).toFixed(2)} KB)`);
    return blob;
  } catch (error: any) {
    logger.error(`❌ Failed to export changes CSV:`, error);
    throw new Error(`Failed to export CSV: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Export all changes for a project as Excel (.xlsx)
 * Recommended for Windows users - styled headers, auto-filters, proper encoding
 */
export async function exportChangesExcel(projectId: string): Promise<Blob> {
  try {
    logger.log(`📥 Exporting changes Excel for project: ${projectId}`);
    const accessToken = localStorage.getItem('access_token');
    const baseURL = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:8000');

    const response = await fetch(
      `${baseURL}/api/v1/monitoring/projects/${projectId}/changes/export/excel`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    logger.log(`✅ Excel export successful (${(blob.size / 1024).toFixed(2)} KB)`);
    return blob;
  } catch (error: any) {
    logger.error(`❌ Failed to export changes Excel:`, error);
    throw new Error(`Failed to export Excel: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Helper function to trigger download of a Blob
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

// ============================================================================
// 🆕 PAGINATED DATA FETCHING
// ============================================================================

/**
 * Get paginated snapshots for a project
 */
export async function getSnapshotsPaginated(
  projectId: string,
  page: number = 1,
  pageSize: number = 50,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc'
): Promise<PaginatedSnapshotsResponse> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    if (sortBy) {
      params.append('sort_by', sortBy);
    }
    if (sortOrder) {
      params.append('sort_order', sortOrder);
    }

    logger.log(`🔍 Fetching paginated snapshots: page ${page}, size ${pageSize}, sort ${sortBy} ${sortOrder}`);
    const response: PaginatedSnapshotsResponse = await monitoringApiService.get(
      `/api/v1/monitoring/projects/${projectId}/snapshots?${params.toString()}`
    );
    logger.log(`✅ Retrieved ${response.items.length} of ${response.total} snapshots`);
    return response;
  } catch (error: any) {
    logger.error(`❌ Failed to fetch paginated snapshots:`, error);
    throw new Error(`Failed to fetch snapshots: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Get paginated changes for a project
 */
export async function getChangesPaginated(
  projectId: string,
  page: number = 1,
  pageSize: number = 50
): Promise<PaginatedChangesResponse> {
  try {
    logger.log(`🔍 Fetching paginated changes: page ${page}, size ${pageSize}`);
    const response = await monitoringApiService.get(
      `/api/v1/monitoring/projects/${projectId}/changes?page=${page}&page_size=${pageSize}`
    );

    // Handle legacy empty array response format
    if (Array.isArray(response)) {
      logger.log(`✅ Retrieved ${response.length} changes (legacy format)`);
      const totalPages = response.length > 0 ? Math.ceil(response.length / pageSize) : 1;
      return {
        items: response,
        total: response.length,
        page: page,
        page_size: pageSize,
        total_pages: totalPages,
        has_next: page < totalPages,
        has_prev: page > 1
      };
    }

    // Handle proper paginated response format
    logger.log(`✅ Retrieved ${response.items.length} of ${response.total} changes`);
    return response as PaginatedChangesResponse;
  } catch (error: any) {
    logger.error(`❌ Failed to fetch paginated changes:`, error);
    throw new Error(`Failed to fetch changes: ${error.message || 'Unknown error'}`);
  }
}

// ============================================================================
// 🎯 MONITORING SCOPE SELECTION API
// ============================================================================

/**
 * Discover URL segments from a project's sitemap
 * Analyzes the sitemap and finds common URL patterns (e.g., /blog, /products)
 */
export async function discoverSegments(projectId: string): Promise<import('./types').SegmentDiscoveryResponse> {
  try {
    logger.log(`🔍 Discovering segments for project: ${projectId}`);
    const response: import('./types').SegmentDiscoveryResponse = await monitoringApiService.post(
      `/api/v1/monitoring/projects/${projectId}/discover-segments`
    );
    logger.log(`✅ Discovered ${response.segments.length} segments (${response.total_urls} total URLs)`);
    return response;
  } catch (error: any) {
    logger.error(`❌ Failed to discover segments:`, error);
    // Return graceful fallback instead of throwing
    return {
      success: false,
      total_urls: 0,
      segments: [],
      error: error.message || 'Failed to discover segments'
    };
  }
}

/**
 * Discover URL segments from a sitemap URL (without project)
 * Used in project wizard before project is created
 * Uses the similarity service endpoint
 */
export async function discoverSegmentsFromUrl(sitemapUrl: string): Promise<import('./types').SegmentDiscoveryResponse> {
  try {
    logger.log(`🔍 Discovering segments from URL: ${sitemapUrl}`);

    // Get auth token
    const accessToken = localStorage.getItem('access_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    // Use the similarity service endpoint (not monitoring)
    const response = await fetch('/api/v1/similarity/sitemap/analyze-segments', {
      method: 'POST',
      headers,
      body: JSON.stringify({ sitemap_url: sitemapUrl })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data: import('./types').SegmentDiscoveryResponse = await response.json();
    logger.log(`✅ Discovered ${data.segments.length} segments (${data.total_urls} total URLs)`);
    return data;
  } catch (error: any) {
    logger.error(`❌ Failed to discover segments from URL:`, error);
    // Return graceful fallback instead of throwing
    return {
      success: false,
      total_urls: 0,
      segments: [],
      error: error.message || 'Failed to discover segments'
    };
  }
}

/**
 * Preview monitoring scope configuration
 * Shows which URLs will be monitored and estimated costs before saving
 */
export async function previewScope(
  projectId: string,
  config: import('./types').ScopePreviewRequest
): Promise<import('./types').ScopePreviewResponse> {
  try {
    logger.log(`🔍 Previewing scope for project: ${projectId}`, config);
    const response: import('./types').ScopePreviewResponse = await monitoringApiService.post(
      `/api/v1/monitoring/projects/${projectId}/scope/preview`,
      config
    );
    logger.log(`✅ Preview: ${response.total_urls} URLs, ${response.estimated_cost_per_crawl} per crawl`);
    return response;
  } catch (error: any) {
    logger.error(`❌ Failed to preview scope:`, error);
    throw new Error(`Failed to preview scope: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Update monitoring scope configuration
 * Saves the scope configuration for a project
 */
export async function updateScope(
  projectId: string,
  config: import('./types').UpdateScopeRequest
): Promise<import('./types').UpdateScopeResponse> {
  try {
    logger.log(`💾 Updating scope for project: ${projectId}`, config);
    const response: import('./types').UpdateScopeResponse = await monitoringApiService.put(
      `/api/v1/monitoring/projects/${projectId}/scope`,
      config
    );
    logger.log(`✅ Scope updated successfully: ${response.message}`);
    return response;
  } catch (error: any) {
    logger.error(`❌ Failed to update scope:`, error);
    throw new Error(`Failed to update scope: ${error.message || 'Unknown error'}`);
  }
}

// ── Unified Scope API (same contract as Similarity & WordCloud) ──────
import { createScopeApi } from './crawlScope';
import { logger } from '../utils/logger';

const MONITORING_SCOPE_API_BASE = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:8000');
export const monitoringScopeApi = createScopeApi(`${MONITORING_SCOPE_API_BASE}/api/v1/monitoring`);

// Legacy exports for backward compatibility
export { getProjects as getMonitoringProjects };
export { getProject as getMonitoringProject };
export { createProject as createMonitoringProject };
export { updateProject as updateMonitoringProject };
export { deleteProject as deleteMonitoringProject };

// Re-export types for convenience
export type { CrawlStatus, CrawlJob, CrawlJobsResponse, ActiveCrawl } from './types';
export type {
  URLHistoryResponse,
  SitemapDiscoveryResponse,
  PaginatedSnapshotsResponse,
  PaginatedChangesResponse,
  TimelineEntry,
  TimelineChange,
  StabilityMetrics,
  AvailabilityHistory,
  FieldHistory,
  SegmentInfo,
  SegmentFilter,
  ScopeType,
  MonitoringScopeConfig,
  SegmentDiscoveryResponse,
  ScopePreviewRequest,
  ScopePreviewResponse,
  UpdateScopeRequest,
  UpdateScopeResponse,
  // 🆕 Tier-based scheduling types
  TierFrequencies,
  PageTierResponse,
  CancelCrawlJobResponse
} from './types';
